import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { BUSINESS_TYPES } from '../mock';
import { useToast } from '../hooks/use-toast';

export default function ContactPage() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');

  const [form, setForm] = useState(() => {
    let initialBusiness = '';
    if (typeParam === 'gearup') initialBusiness = 'GearUp! — Last-Mile Mobility';
    else if (typeParam === 'campus') initialBusiness = 'Campus Go! — Campus Mobility';
    else if (typeParam === 'orbit') initialBusiness = 'Orbit — Everyday Commute';
    else if (typeParam === 'host') initialBusiness = 'Asset Partnership / Host';

    return { name: '', phone: '', city: '', business: initialBusiness, message: '' };
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city || !form.business) {
      toast({ title: 'Missing fields', description: 'Please fill all required fields.' });
      return;
    }

    const emailSubject = encodeURIComponent("E Drives - New Enquiry Request");
    const emailBody = encodeURIComponent(
      `Hi E Drives Team,\n\n` +
      `Here is a new mobility enquiry details:\n` +
      `- Name: ${form.name}\n` +
      `- Phone: ${form.phone}\n` +
      `- City: ${form.city}\n` +
      `- Interested In: ${form.business}\n` +
      `- Message: ${form.message || 'No additional message provided.'}\n\n` +
      `Regards`
    );

    // Open user's default mail application with pre-filled content
    window.location.href = `mailto:info@edrives.in?subject=${emailSubject}&body=${emailBody}`;

    toast({ title: 'Opening Mail Client', description: 'Please click send on the pre-filled draft.' });
    setForm({ name: '', phone: '', city: '', business: '', message: '' });
  };

  return (
    <>
      <section className="pt-36 pb-12 bg-brand-mint relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="heading-display text-4xl md:text-6xl text-brand-dark">Let’s Move Mobility Forward.</h1>
          <p className="mt-4 text-brand-dark/70 max-w-2xl mx-auto">Whether you're exploring our mobility services, asset partnerships or business opportunities — we'd love to hear from you.</p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8 overflow-hidden">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="lg:col-span-2 bg-brand-lavenderSoft rounded-3xl p-8 border border-brand-dark/5 shadow-sm"
          >
            <h2 className="heading-display text-2xl text-brand-dark">Start a Conversation</h2>
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-brand-dark">Name *</label>
                <input value={form.name} onChange={update('name')} className="px-4 py-3 rounded-xl border border-brand-dark/15 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-shadow" placeholder="Your full name" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-brand-dark">Phone *</label>
                <input value={form.phone} onChange={update('phone')} className="px-4 py-3 rounded-xl border border-brand-dark/15 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-shadow" placeholder="+91 XXXXX XXXXX" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-brand-dark">City *</label>
                <input value={form.city} onChange={update('city')} className="px-4 py-3 rounded-xl border border-brand-dark/15 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-shadow" placeholder="Your city" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-brand-dark">I’m Interested In *</label>
                <select value={form.business} onChange={update('business')} className="px-4 py-3 rounded-xl border border-brand-dark/15 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-shadow">
                  <option value="">Select Option</option>
                  {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="text-sm font-medium text-brand-dark">Tell Us More</label>
                <textarea value={form.message} onChange={update('message')} rows={4} className="px-4 py-3 rounded-xl border border-brand-dark/15 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 transition-shadow" placeholder="Tell us how you'd like to connect with E Drives..." />
              </div>
              <div className="md:col-span-2">
                <button type="submit" className="btn-dark-pill" disabled={submitted}>
                  {submitted ? <><CheckCircle2 size={18} /> Sent</> : <>Submit Enquiry <ArrowRight size={16} /></>}
                </button>
              </div>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.05 }}
            className="space-y-4"
          >
            <h2 className="heading-display text-2xl text-brand-dark">Get In Touch</h2>

            <motion.div
              className="bg-white rounded-2xl p-5 border border-brand-dark/10 shadow-sm hover:shadow-md hover:-translate-y-1 hover:scale-[1.01] transition-all duration-300"
            >
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-lg bg-brand-mint text-brand-dark flex items-center justify-center shadow-sm"><Mail size={18} /></div>
                <div>
                  <p className="text-xs text-brand-dark/60">Email</p>
                  <a href="mailto:info@edrives.in" className="font-semibold text-brand-dark hover:text-brand-green">info@edrives.in</a>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-5 border border-brand-dark/10 shadow-sm hover:shadow-md hover:-translate-y-1 hover:scale-[1.01] transition-all duration-300"
            >
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-lg bg-brand-mint text-brand-dark flex items-center justify-center shadow-sm"><Phone size={18} /></div>
                <div>
                  <p className="text-xs text-brand-dark/60">Phone</p>
                  <a href="tel:+918867208322" className="font-semibold text-brand-dark hover:text-brand-green">+91-8867208322</a>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-5 border border-brand-dark/10 shadow-sm hover:shadow-md hover:-translate-y-1 hover:scale-[1.01] transition-all duration-300"
            >
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-lg bg-brand-mint text-brand-dark flex items-center justify-center shadow-sm"><MapPin size={18} /></div>
                <div>
                  <p className="text-xs text-brand-dark/60">Registered Address</p>
                  <p className="font-semibold text-sm text-brand-dark leading-relaxed mt-1">
                    Site No. 21, 733/A, HMT Road, Ramaiah Bus Stop,<br />
                    Gokula 1st Stage, 2nd Phase, Divanarapalya,<br />
                    Mathikere, Bangalore North, Karnataka - 560054
                  </p>
                </div>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </section>
    </>
  );
}
